import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle2, Scissors, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Platform caption rules:
//   • Facebook: feed shows ~280 chars before "See more"; absolute limit 63,206
//   • Instagram: feed shows ~125 chars before "more"; absolute limit 2,200; ≤30 hashtags
//   • Threads: HARD cap 500 chars total (including hashtags). No truncation — post fails over.
const PLATFORM_LIMITS = {
  facebook: { soft: 280, hard: 63206, hashtagCap: null, name: 'Facebook' },
  instagram: { soft: 125, hard: 2200, hashtagCap: 30, name: 'Instagram' },
  threads: { soft: 500, hard: 500, hashtagCap: null, name: 'Threads' },
};

const buildFullCaption = (caption, hashtags) => {
  if (!hashtags?.length) return caption || '';
  return `${caption || ''}\n\n${hashtags.map((h) => `#${h}`).join(' ')}`;
};

const truncate = (str, n) => (str.length <= n ? str : str.slice(0, n - 1).trimEnd() + '…');

/** Trim caption+hashtags to fit a platform.
 *  1. IG hashtag cap: drop excess hashtags from end.
 *  2. Total over hard limit: trim caption first (keep hashtags for discovery).
 *  3. Still over: drop hashtags from end. */
const trimToFit = (caption, hashtags, limits) => {
  let outCaption = caption || '';
  let outHashtags = [...(hashtags || [])];

  if (limits.hashtagCap && outHashtags.length > limits.hashtagCap) {
    outHashtags = outHashtags.slice(0, limits.hashtagCap);
  }

  const totalLen = () => buildFullCaption(outCaption, outHashtags).length;

  if (totalLen() > limits.hard) {
    const hashtagBlock = outHashtags.length
      ? `\n\n${outHashtags.map((h) => `#${h}`).join(' ')}`
      : '';
    const room = limits.hard - hashtagBlock.length - 1;
    outCaption = room > 10 ? truncate(outCaption, room) : '';
  }

  while (totalLen() > limits.hard && outHashtags.length > 0) {
    outHashtags.pop();
  }

  return { caption: outCaption, hashtags: outHashtags };
};

const PlatformMockup = ({ platform, imageUrl, videoUrl, isReel, originalCaption, originalHashtags, trimmed, onTrim, onResetTrim, onCopyTrimmed }) => {
  const limits = PLATFORM_LIMITS[platform];
  const displayCaption = trimmed?.caption ?? originalCaption;
  const displayHashtags = trimmed?.hashtags ?? originalHashtags;
  const full = buildFullCaption(displayCaption, displayHashtags);
  const len = full.length;
  const overHard = len > limits.hard;
  const overSoft = len > limits.soft;
  const visible = overSoft ? truncate(full, limits.soft) : full;
  const hashtagCount = displayHashtags?.length || 0;
  const overHashtagCap = limits.hashtagCap && hashtagCount > limits.hashtagCap;
  const wasTrimmed = !!trimmed;
  const needsTrim = overHard || overHashtagCap;

  const imageBox = {
    facebook: 'aspect-square sm:aspect-[4/3]',
    instagram: 'aspect-square',
    threads: 'aspect-square',
  }[platform];

  const accent = {
    facebook: 'border-[#1877F2]',
    instagram: 'border-[#E4405F]',
    threads: 'border-black',
  }[platform];

  const headerBg = {
    facebook: 'bg-[#1877F2] text-white',
    instagram: 'bg-gradient-to-r from-[#FFDC80] via-[#F77737] to-[#E4405F] text-white',
    threads: 'bg-black text-white',
  }[platform];

  return (
    <div className={`border-2 rounded-lg overflow-hidden ${accent} bg-white flex flex-col`} data-testid={`mockup-${platform}`}>
      <div className={`px-3 py-2 text-xs font-medium ${headerBg} flex items-center justify-between`}>
        <span>{limits.name} preview {wasTrimmed && '• trimmed'}</span>
        {overHard ? (
          <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> over limit</span>
        ) : (
          <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> ok</span>
        )}
      </div>

      <div className="p-2 flex items-center gap-2 text-xs">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#00E5CC] to-[#00A896] flex-shrink-0" />
        <div className="min-w-0">
          <p className="font-semibold truncate">Clear Communication App</p>
          <p className="text-slate-500">Just now</p>
        </div>
      </div>

      <div className={`bg-slate-100 ${imageBox} overflow-hidden`}>
        {isReel && videoUrl ? (
          <video src={`${API}${videoUrl}`} poster={`${API}${imageUrl}`} controls playsInline className="w-full h-full object-cover" />
        ) : (
          <img src={`${API}${imageUrl}`} alt="" className="w-full h-full object-cover" />
        )}
      </div>

      <div className="p-2.5 text-xs space-y-1 flex-1">
        <p className="whitespace-pre-wrap leading-relaxed text-slate-700" data-testid={`mockup-${platform}-caption`}>
          {visible}
          {overSoft && !overHard && <span className="text-slate-400"> more</span>}
        </p>
        <div className="flex items-center gap-2 pt-1 flex-wrap text-[10px]">
          <Badge variant="outline" className={`text-[10px] py-0 h-4 ${overHard ? 'border-red-500 text-red-600' : overSoft ? 'border-amber-500 text-amber-700' : 'border-slate-300 text-slate-500'}`}>
            {len} / {limits.hard} chars
          </Badge>
          {hashtagCount > 0 && (
            <Badge variant="outline" className={`text-[10px] py-0 h-4 ${overHashtagCap ? 'border-red-500 text-red-600' : 'border-slate-300 text-slate-500'}`}>
              {hashtagCount} hashtag{hashtagCount === 1 ? '' : 's'}
              {limits.hashtagCap && ` / ${limits.hashtagCap}`}
            </Badge>
          )}
        </div>
        {overHard && (
          <p className="text-red-600 text-[11px] flex items-start gap-1 mt-1">
            <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>
              {platform === 'threads'
                ? `Cut ${len - limits.hard} chars — Threads will REJECT this post.`
                : `Cut ${len - limits.hard} chars — over absolute limit.`}
            </span>
          </p>
        )}
        {overHashtagCap && (
          <p className="text-red-600 text-[11px] flex items-start gap-1">
            <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>Remove {hashtagCount - limits.hashtagCap} hashtags — IG caps at {limits.hashtagCap}.</span>
          </p>
        )}
      </div>

      <div className="border-t p-2 flex gap-1.5 flex-wrap">
        {needsTrim && !wasTrimmed && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onTrim} data-testid={`trim-fit-${platform}`}>
            <Scissors className="h-3 w-3 mr-1" /> Trim to fit
          </Button>
        )}
        {wasTrimmed && (
          <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={onResetTrim} data-testid={`reset-trim-${platform}`}>
            Reset
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-7 text-xs ml-auto" onClick={onCopyTrimmed} data-testid={`copy-${platform}`}>
          <Copy className="h-3 w-3 mr-1" /> Copy
        </Button>
      </div>
    </div>
  );
};

const CrossPostPreviewDialog = ({ draft, open, onClose }) => {
  const [trimmedByPlatform, setTrimmedByPlatform] = useState({
    facebook: null, instagram: null, threads: null,
  });

  useEffect(() => {
    setTrimmedByPlatform({ facebook: null, instagram: null, threads: null });
  }, [draft?.draft_id]);

  if (!draft) return null;

  const trim = (platform) => {
    const result = trimToFit(draft.caption || '', draft.hashtags || [], PLATFORM_LIMITS[platform]);
    setTrimmedByPlatform((s) => ({ ...s, [platform]: result }));
    toast.success(`Trimmed for ${PLATFORM_LIMITS[platform].name}`);
  };

  const resetTrim = (platform) => {
    setTrimmedByPlatform((s) => ({ ...s, [platform]: null }));
  };

  const copyCaption = (platform) => {
    const t = trimmedByPlatform[platform];
    const cap = t?.caption ?? draft.caption;
    const tags = t?.hashtags ?? draft.hashtags;
    const full = buildFullCaption(cap, tags);
    navigator.clipboard.writeText(full).then(
      () => toast.success(`${PLATFORM_LIMITS[platform].name} version copied`),
      () => toast.error('Copy failed'),
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="cross-post-preview-dialog">
        <DialogHeader>
          <DialogTitle>Cross-post preview — {draft.title || draft.caption?.slice(0, 60)}</DialogTitle>
          <DialogDescription>
            See how this post renders on each platform. Click "Trim to fit" to auto-shorten for any platform that's over its limit, then "Copy" to get the platform-specific version for manual posting.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
          {['facebook', 'instagram', 'threads'].map((p) => (
            <PlatformMockup
              key={p}
              platform={p}
              imageUrl={draft.image_url}
              videoUrl={draft.video_url}
              isReel={!!draft.is_reel}
              originalCaption={draft.caption}
              originalHashtags={draft.hashtags}
              trimmed={trimmedByPlatform[p]}
              onTrim={() => trim(p)}
              onResetTrim={() => resetTrim(p)}
              onCopyTrimmed={() => copyCaption(p)}
            />
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Tip: Threads' 500-char hard limit includes hashtags. Trim trims the caption first (preserving hashtags for discovery), then drops hashtags from the end if still over.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default CrossPostPreviewDialog;
